export type HandledErrorContext = {
  feature: string
  action: string
  layer: "client" | "server" | "loader"
  fallback?: string
  extra?: Record<string, unknown>
}

type ScopeLike = {
  setTag: (key: string, value: string) => void
  setContext: (key: string, context: Record<string, unknown>) => void
}

type SentryLike = {
  captureException: (error: unknown) => void
  flush?: (timeout?: number) => Promise<unknown>
  withScope: (callback: (scope: ScopeLike) => void) => void
}

declare global {
  var __handledErrorsImport__: ((specifier: string) => Promise<SentryLike>) | undefined
}

async function importAtRuntime(specifier: string): Promise<SentryLike> {
  if (typeof globalThis.__handledErrorsImport__ === "function") {
    return globalThis.__handledErrorsImport__(specifier)
  }

  return (await import(/* @vite-ignore */ specifier)) as SentryLike
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error
  return new Error(String(error))
}

async function loadSentry(): Promise<{ isServer: boolean; sentry: SentryLike }> {
  if (typeof window === "undefined") {
    return {
      isServer: true,
      sentry: await importAtRuntime("@sentry/node"),
    }
  }

  return {
    isServer: false,
    sentry: await import("@sentry/react"),
  }
}

export async function captureHandledException(
  error: unknown,
  context: HandledErrorContext,
): Promise<void> {
  const normalizedError = normalizeError(error)
  const { isServer, sentry } = await loadSentry()

  sentry.withScope((scope) => {
    scope.setTag("handled", "true")
    scope.setTag("feature", context.feature)
    scope.setTag("action", context.action)
    scope.setTag("layer", context.layer)

    const errorContext: Record<string, unknown> = {}
    if (context.fallback !== undefined) {
      errorContext.fallback = context.fallback
    }
    if (context.extra !== undefined) {
      errorContext.extra = context.extra
    }
    if (Object.keys(errorContext).length > 0) {
      scope.setContext("handled_error", errorContext)
    }

    sentry.captureException(normalizedError)
  })

  if (isServer && typeof sentry.flush === "function") {
    await sentry.flush(2000)
  }
}

export async function withCapturedException<T>(
  context: HandledErrorContext,
  fn: () => Promise<T>,
): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    await captureHandledException(error, context)
    throw error
  }
}
